/** Work Status exclusive flags: Full / TTD / TPD / PPD / Retired */

import moment from 'moment';

const isOn = v => v === true || v === 'true' || v === 1 || v === '1';

const hasData = v => {
  if (v === null || v === undefined || v === '' || v === false || v === 'false') return false;
  return true;
};

/** Radio values for Full Duty — share DB field ws2 with the optional date (alternatives). */
export const FULL_DUTY_REASON_VALUES = [
  'The patient may return to full duty due to new job.',
  'The patient may return to full duty due to fear of losing current job.',
  "The patient may return to full duty due to patient's request.",
];

export const isFullDutyReason = v => FULL_DUTY_REASON_VALUES.includes(v);

const serializeFullDutyDate = v => {
  if (v === null || v === undefined || v === '') return '';
  if (moment.isMoment(v)) return v.isValid() ? v.format('MM/DD/YYYY') : '';
  const m = moment(v);
  return m.isValid() ? m.format('MM/DD/YYYY') : '';
};

/**
 * Collapse UI fullDuty (reason) + fullDutyDate into a single ws2 string for the API.
 * Always returns a string ('' clears leftover DB values — never undefined).
 * Reason and date are alternatives; if both are set, date wins.
 */
export const resolveFullDutyWs2 = (fullDuty, fullDutyDate) => {
  const dateStr = serializeFullDutyDate(fullDutyDate);
  if (dateStr) return dateStr;
  if (fullDuty != null && String(fullDuty).trim() !== '') return String(fullDuty).trim();
  return '';
};

/** Split stored ws2 into form fields (use null so Ant Design clears Radio/DatePicker). */
export const splitWs2ForForm = (ws2, { isDate, parseDateField }) => {
  if (ws2 == null || ws2 === '') {
    return { fullDuty: null, fullDutyDate: null };
  }
  if (isFullDutyReason(ws2)) {
    return { fullDuty: ws2, fullDutyDate: null };
  }
  if (isDate(ws2)) {
    return { fullDuty: null, fullDutyDate: parseDateField(ws2) };
  }
  // Legacy free-text reason
  return { fullDuty: ws2, fullDutyDate: null };
};

/**
 * Pick one status when multiple are on.
 * Prefer the one with supporting dates/values; else form order (Full → TTD → TPD → PPD → Retired).
 * Clears sibling flags and inactive status date fields.
 */
export const resolveExclusiveWorkStatus = workstatus => {
  const ws = { ...(workstatus || {}) };

  const candidates = [];
  if (isOn(ws.ws1)) candidates.push({ key: 'ws1', score: hasData(ws.ws2) ? 2 : 1, order: 0 });
  if (isOn(ws.ws3)) candidates.push({ key: 'ws3', score: hasData(ws.ws4) || hasData(ws.ws5) ? 2 : 1, order: 1 });
  if (isOn(ws.ws6)) candidates.push({ key: 'ws6', score: hasData(ws.ws7) || hasData(ws.ws8) ? 2 : 1, order: 2 });
  if (isOn(ws.ws45)) candidates.push({ key: 'ws45', score: hasData(ws.ws47) ? 2 : 1, order: 3 });
  if (isOn(ws.ws46)) candidates.push({ key: 'ws46', score: 1, order: 4 });

  if (candidates.length === 0) {
    return ws;
  }

  candidates.sort((a, b) => b.score - a.score || a.order - b.order);
  const keep = candidates[0].key;

  ws.ws1 = keep === 'ws1';
  ws.ws3 = keep === 'ws3';
  ws.ws6 = keep === 'ws6';
  ws.ws45 = keep === 'ws45';
  ws.ws46 = keep === 'ws46';

  if (keep !== 'ws1') {
    ws.ws2 = '';
    ws.fullDuty = null;
    ws.fullDutyDate = null;
  }
  if (keep !== 'ws3') {
    ws.ws4 = '';
    ws.ws5 = '';
  }
  if (keep !== 'ws6') {
    ws.ws7 = '';
    ws.ws8 = '';
  }

  // Stale legacy MMI/P&S switch must not survive when Full / TTD / TPD is the active status
  if (keep === 'ws1' || keep === 'ws3' || keep === 'ws6') {
    ws.ws36 = false;
  }

  return ws;
};

export const isWorkStatusFlagOn = isOn;
